// X6 — every document's window at once, on one shared scale.
//
// NO NEW ENDPOINT. `GET /dashboards/:org/docs` already returns
// `(doc_id, date, unique_sessions)` and `useDocTimeline` already fetches it for
// `DocumentCoverage`. This is a second reading of the same payload.
//
// THE SHARED SCALE IS THE WHOLE POINT, and it is the one thing a reader has to
// be told. Given a row each, a document with 30 visits and one with 1,700 both
// fill their own band and read as equally busy — the chart then says the
// opposite of what the data says. `Ridge` computes one ceiling across every row
// and the subtitle states it in words.
//
// A DAY WITH NO ROW IS A GAP, NOT A ZERO. A document published thirty days into
// a ninety-day window has no traffic to report for the first sixty, and a line
// along the axis there claims nobody read it. The dense-map below fills only
// the days the rollup actually returned.
//
// Capped, and the cap is stated. A corpus of 32 documents is a wall of rows;
// the busiest twelve are the ones a writer can act on, and `shownOf` makes the
// truncation visible rather than letting a partial list read as complete.

import { Card, EmptyState } from "../../ui";
import { Ridge, type RidgeRow } from "../../charts";
import type { DocMetric } from "../../dashboard/api";
import { formatCount, shownOf } from "./format";
import {
  RIDGE_EMPTY_BODY,
  RIDGE_EMPTY_HEADLINE,
  RIDGE_SUBTITLE,
  RIDGE_TITLE,
} from "./metricLabels";
import "./analytics.css";

const TOP_N = 12;

const DAY_MS = 86_400_000;

/** Every ISO date from `first` to `last` inclusive.
 *
 * Parsed as UTC (`Date.parse` of a bare `YYYY-MM-DD` is UTC by spec) and
 * stepped in whole days, so no timezone offset can drop or duplicate one — the
 * classic way a calendar axis loses the 31st or gains a leap day.
 */
function denseDates(first: string, last: string): string[] {
  const out: string[] = [];
  for (let t = Date.parse(first); t <= Date.parse(last); t += DAY_MS) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

/** Timeline rows -> one dense, gap-preserving series per document, busiest
 *  first.
 *
 * Exported for the test: the gap behaviour and the ordering are the contract,
 * and asserting them through a rendered path would pin the drawing.
 */
export function ridgeRows(
  timeline: DocMetric[],
  limit = TOP_N,
): { rows: RidgeRow[]; total: number } {
  // A DENSE CALENDAR, not the dates that happen to appear in the payload.
  // The rollup writes no row for a day nothing was read, so keying the axis on
  // the distinct dates present would silently close those gaps up — a corpus
  // read on Mondays only would draw as continuous daily traffic, and a
  // fortnight's outage would vanish rather than showing as one.
  const present = [...new Set(timeline.map((row) => row.date))].sort();
  const dates = present.length ? denseDates(present[0], present[present.length - 1]) : [];
  const index = new Map(dates.map((date, position) => [date, position]));
  const byDoc = new Map<string, Array<number | null>>();
  const totals = new Map<string, number>();

  for (const row of timeline) {
    let series = byDoc.get(row.doc_id);
    if (!series) {
      // `null`, not 0. A day the rollup returned no row for is a day we did
      // not measure, and every unfilled slot must stay a gap.
      series = new Array<number | null>(dates.length).fill(null);
      byDoc.set(row.doc_id, series);
    }
    series[index.get(row.date) as number] = row.unique_sessions;
    totals.set(row.doc_id, (totals.get(row.doc_id) ?? 0) + row.unique_sessions);
  }

  const rows = [...byDoc.entries()]
    .sort((a, b) => (totals.get(b[0]) ?? 0) - (totals.get(a[0]) ?? 0))
    .map(([docId, values]) => ({
      key: docId,
      label: docId,
      values,
    }));

  return { rows: rows.slice(0, limit), total: rows.length };
}

export type DocumentRidgelineProps = {
  timeline: DocMetric[];
  days: number;
};

export function DocumentRidgeline({ timeline, days }: DocumentRidgelineProps) {
  const { rows, total } = ridgeRows(timeline);

  if (rows.length === 0) {
    return (
      <Card header={RIDGE_TITLE}>
        <EmptyState headline={RIDGE_EMPTY_HEADLINE} body={RIDGE_EMPTY_BODY} />
      </Card>
    );
  }

  const busiest = rows[0].values.reduce<number>(
    (top, value) => (value !== null && value > top ? value : top),
    0,
  );

  return (
    <Card header={RIDGE_TITLE}>
      <p className="type-bodyS maisight-analytics-muted">{RIDGE_SUBTITLE}</p>
      <div className="maisight-analytics-ridge">
        <ol className="maisight-analytics-ridge-labels type-numSm">
          {rows.map((row) => (
            <li key={row.key} className="maisight-analytics-term">
              {row.label}
            </li>
          ))}
        </ol>
        <Ridge
          label={
            `Visits per day for ${rows.length} documents over the last ${days} days,` +
            ` all on one scale topping out at ${formatCount(busiest)} visits in a day`
          }
          rows={rows}
        />
      </div>
      <div className="maisight-analytics-card-footer type-bodyS">
        <span>{shownOf(rows.length, total, "document")}</span>
      </div>
    </Card>
  );
}
